// Cut-off display logic for the sailing card.
//
// The cut-off cell used to have exactly two states: a date, or the words "not published". That second
// state was doing too much work. Most of the sailings behind it are HMM and OOCL rows we rebuilt from a
// slot partner's schedule (source='derived'), where we never held the carrier's cut-off at all. Telling
// a forwarder the carrier published no deadline, when the truth is that we did not fetch one, is the
// gap this module closes. The API decides which silence it is; this turns that into words.

import type { CardCutoffProvenance, CutoffStatus, Partner } from '../components/SailingCard'
import { shortDate } from './site-dates'

export interface Cutoffs {
  cy: string | null
  doc: string | null
  vgm: string | null
}

/** The cut-off that bites first. `doc` shows as SI, the name forwarders use for it. */
export function nearestCutoff(c: Cutoffs, nowMs: number = Date.now()) {
  const items = (
    [
      ['CY', c.cy],
      ['SI', c.doc],
      ['VGM', c.vgm],
    ] as [string, string | null][]
  ).filter(([, v]) => v) as [string, string][]
  if (!items.length) return null
  items.sort((a, b) => new Date(a[1]).getTime() - new Date(b[1]).getTime())
  const [label, when] = items[0] as [string, string]
  const days = Math.round((new Date(when).getTime() - nowMs) / 86_400_000)
  return { label, when, days }
}

export interface CutoffCell {
  /** Short label under the empty cell. Never "not published" unless that is what happened. */
  label: string
  /** The longer explanation, for the tooltip. Null when there is nothing to explain. */
  detail: string | null
}

/**
 * What to say when there is no card-level cut-off to show.
 *
 * `carriers` are the slot partners on this sailing, each carrying its own provenance from the API.
 * `provenance` is the card-level verdict for the cut-off kind, when the API sent one.
 */
export function emptyCutoffCell(
  carriers: Partner[],
  provenance?: CardCutoffProvenance | undefined
): CutoffCell {
  if (provenance?.status === 'disagree') {
    return {
      label: 'varies by carrier',
      detail:
        'The carriers selling this vessel publish different cut-offs. Each sets its own against its own slot allocation, so there is no single one for the sailing. Open the carrier list below.',
    }
  }

  const kinds = new Set(carriers.map((p) => p.cutoff_status).filter(Boolean) as CutoffStatus[])
  // No status at all: an older API, so keep the wording we have always shown rather than making a
  // claim the payload does not support.
  if (!kinds.size) return { label: 'not published', detail: null }

  // The sentences live here now. The API sends the reason as one word and we say it in our own words,
  // which is also why it no longer ships a `note` per row.
  if (kinds.has('not_held') && !kinds.has('not_published')) {
    return {
      label: 'not held',
      detail:
        "We rebuilt this sailing from the schedule of another line selling the same ship, so we never held these carriers' own cut-offs. The carriers may well publish them.",
    }
  }

  if (kinds.has('not_held')) {
    return {
      label: 'partly held',
      detail:
        'Some carriers on this vessel published no cut-off, and for the others we hold no cut-off at all because we rebuilt their sailing from a slot partner. See each carrier below.',
    }
  }

  return {
    label: 'not published',
    detail:
      "We hold each carrier's own schedule for this sailing and none of them carries a cut-off time.",
  }
}

/** One-word state for the per-carrier line in the partner list. */
export function partnerCutoffLabel(p: Partner, nowMs: number = Date.now()): CutoffCell {
  const own = p.cutoffs ? nearestCutoff(p.cutoffs, nowMs) : null
  if (own) return { label: `${own.label} ${fmtShort(own.when)}`, detail: null }
  const status = p.cutoff_status
  if (!status) return { label: '', detail: null }
  if (status === 'not_held')
    return {
      label: 'cut-off not held',
      detail: `This sailing came to us from another line selling the same ship, so we never held ${p.carrier_name}'s own cut-off.`,
    }
  if (status === 'not_published')
    return {
      label: 'no cut-off published',
      detail: `We hold ${p.carrier_name}'s own schedule for this sailing and it carries no cut-off time.`,
    }
  return { label: '', detail: null }
}

function fmtShort(iso: string): string {
  return shortDate(iso)
}
